import { useState } from "react"
import { Input, message, Modal, Typography } from "antd"
import { CopyOutlined } from "@ant-design/icons"
import { addDoc, collection, getDocs, query, Timestamp, where } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Link } = Typography

export default function DuplicatePrice({ record }) {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newPriceName, setNewPriceName] = useState("")
  const [loading, setLoading] = useState(false)

  // Function to generate a unique copy name with consistent numbering
  const generateCopyName = async (originalName) => {
    try {
      const priceRef = collection(db, "priceCalculation")

      // Get all existing copies for this user
      const q = query(priceRef, where("user", "==", record.user))
      const snapshot = await getDocs(q)

      // Find all existing copy numbers for this base name
      const existingCopyNumbers = []
      const copyPattern = new RegExp(`^${originalName}_copy(\\d+)$`)

      snapshot.docs.forEach((doc) => {
        const productName = doc.data().product?.productName
        if (productName === originalName) {
          // Original exists
          existingCopyNumbers.push(0)
        } else if (productName) {
          const match = productName.match(copyPattern)
          if (match) {
            existingCopyNumbers.push(parseInt(match[1]))
          }
        }
      })

      // Find the next available number
      let copyNumber = 1
      while (existingCopyNumbers.includes(copyNumber)) {
        copyNumber++
      }

      return `${originalName}_copy${copyNumber}`
    } catch (error) {
      console.error("Error checking name:", error)
      return `${originalName}_copy1`
    }
  }

  // Open modal and set default name
  const handleOpenModal = async () => {
    setLoading(true)
    try {
      const defaultName = await generateCopyName(record.product.productName)
      setNewPriceName(defaultName)
      setIsModalVisible(true)
    } catch (error) {
      message.error("Error generating copy name")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Handle the duplication
  const handleDuplicate = async () => {
    if (!newPriceName.trim()) {
      message.warning("Please enter a name for the duplicated price")
      return
    }

    setLoading(true)
    try {
      // Create immediate timestamp to match existing document structure
      const now = Timestamp.now()

      // Create a new object based on the original record
      const newPriceData = {
        ...record,
        // Update the product name
        product: {
          ...record.product,
          productName: newPriceName.trim(),
        },
        // Reset specific fields
        technicalPage: null,
        hasTechincalPage: 0,
        activeOrderCount: 0,
        currentlyInOrders: [],
        hasDoneOrders: false,
        // Update timestamps with immediate values (matching original structure)
        timestamp: now,
        date: {
          status: 0,
          lastEdit: now,
          created: now,
        },
      }

      // Remove the id field
      delete newPriceData.id

      // Add the new document to Firestore
      const priceRef = collection(db, "priceCalculation")
      await addDoc(priceRef, newPriceData)

      message.success("Price duplicated successfully!")
      setIsModalVisible(false)
      setNewPriceName("")
    } catch (error) {
      message.error("Error duplicating price")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setNewPriceName("")
  }

  return (
    <>
      <Link
        size="small"
        icon={<CopyOutlined />}
        onClick={handleOpenModal}
        loading={loading}
        type="default">
        <CopyOutlined /> Duplicate Price
      </Link>

      <Modal
        title="Duplicate Price"
        open={isModalVisible}
        onOk={handleDuplicate}
        onCancel={handleCancel}
        okText="Duplicate"
        cancelText="Cancel"
        confirmLoading={loading}>
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
            New Price Name:
          </label>
          <Input
            placeholder="Enter new price name"
            value={newPriceName}
            onChange={(e) => setNewPriceName(e.target.value)}
            onPressEnter={handleDuplicate}
          />
        </div>
      </Modal>
    </>
  )
}
